import React from 'react';

import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth';
import AdditionalServices from 'src/features/parkingManagement/components/AdditionalServices/AdditionalServices';
import AllowListDashboard from 'src/features/parkingManagement/components/AllowLists/AllowListDashboard';
import AllowListsTable from 'src/features/parkingManagement/components/AllowLists/table/AllowListsTable';
import Amenities from 'src/features/parkingManagement/components/Amenities/Amenities';
import EntryExitPoints from 'src/features/parkingManagement/components/EntryExitGateways/EntryExitPoints';
import GeneralSettings from 'src/features/parkingManagement/components/forms/GeneralSettings';
import Integrations from 'src/features/parkingManagement/components/forms/Integrations';
import PaymentProviders from 'src/features/parkingManagement/components/forms/PaymentProviders';
import Zones from 'src/features/parkingManagement/components/forms/Zones';
import Notifications from 'src/features/parkingManagement/components/Notifications/Notifications';
import ParkingDetails from 'src/features/parkingManagement/components/ParkingDetails';
import ParkingLayout from 'src/features/parkingManagement/components/ParkingLayout';
import ParkingManagement from 'src/features/parkingManagement/components/ParkingManagement';
import { ParkingManagementLayout } from 'src/features/parkingManagement/components/ParkingManagementLayout';
import PointsGroupTable from 'src/features/parkingManagement/components/PointsGroup/tables/PointsGroupTable';
import PointsOfInterest from 'src/features/parkingManagement/components/PointsOfInterest/PointsOfInterest';
import ReasonsForGuest from 'src/features/parkingManagement/components/ReasonsForGuest/ReasonsForGuest';
import ZoneUserAccessTable from 'src/features/parkingManagement/components/ZoneUserAccess/tables/ZoneUserAccessTable';
import PointsTable from 'src/features/points/components/tables/PointsTable';
import ParkingPricingDetails from 'src/features/pricingPlans/components/ParkingPricingDetails';
import ParkingPricingPlans from 'src/features/pricingPlans/components/ParkingPricingPlans';
import ParkingPricingPlansByPoints from 'src/features/pricingPlans/components/ParkingPricingPlansByPoints';
import ParkingPricingPlansLayout from 'src/features/pricingPlans/routes/ParkingPricingPlansLayout';
import FixedReservationTypes from 'src/features/reservations/components/tables/FixedReservationTypes';
import ReservationsTypeTable from 'src/features/reservations/components/tables/ReservationsTypeTable';
import ReservationVersions from 'src/features/reservations/components/tables/ReservationVersions';
import ReservationTypeDetails from 'src/features/reservations/routes/ReservationTypeDetails';
import ReservationTypesLayout from 'src/features/reservations/routes/ReservationTypesLayout';
import SelectedReservationTypeLayout from 'src/features/reservations/routes/SelectedReservationTypeLayout';
import SubscriptionPlansTable from 'src/features/subscriptions/components/tables/SubscriptionPlansTable';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useSidePanel } from 'src/registry/providers/SidePanelProvider';

const ParkingManagementRoutes = () => {
    const permissionDetailsManage = useHasPermissions([PERMISSION.PARKING_DETAILS_MANAGE]);
    const permissionGuestReasonsManage = useHasPermissions([PERMISSION.GUEST_REASONS_MANAGE]);
    const permissionPricing = useHasPermissions([PERMISSION.PRICING]);
    const permissionReservationsSettings = useHasPermissions([PERMISSION.RESERVATIONS_SETTINGS]);
    const permissionParkingAdmin = useHasPermissions([PERMISSION.PARKING_ADMIN_SETTINGS]);
    const permissionSubscriptions = useHasPermissions([PERMISSION.SUBSCRIPTIONS_SETTINGS]);
    const permissionNotificationSettings = useHasPermissions([PERMISSION.PARKING_NOTIFICATIONS_SETTINGS]);
    const permissionAdditionalServices = useHasPermissions([PERMISSION.ADDITIONAL_SERVICES_MANAGE]);
    const permissionUsersManage = useHasPermissions([PERMISSION.USERS_MANAGE]);

    const location = useLocation();

    const isAllParkingsUrlPath = location.pathname === '/parking-management';

    const { setForceLeftStaticPanel } = useSidePanel();

    React.useEffect(() => {
        if (!isAllParkingsUrlPath) {
            setForceLeftStaticPanel(true);
        }
        return () => {
            setForceLeftStaticPanel(false);
        };
    }, [isAllParkingsUrlPath]);

    const hasAccess =
        permissionDetailsManage ||
        permissionGuestReasonsManage ||
        permissionPricing ||
        permissionReservationsSettings ||
        permissionParkingAdmin ||
        permissionSubscriptions ||
        permissionNotificationSettings ||
        permissionAdditionalServices ||
        permissionUsersManage;

    return hasAccess ? (
        <Routes>
            <Route path="" element={<ParkingManagementLayout />}>
                <Route path=":parkingId" element={<ParkingLayout />} />
                <Route path="" element={<ParkingManagement />} />
                <Route path="*" element={<Navigate to=".." />} />
            </Route>

            <Route path=":parkingId" element={<ParkingLayout />}>
                {permissionDetailsManage && <Route path="" element={<Zones />} />}
                {permissionDetailsManage && <Route path="points" element={<PointsTable />} />}
                {permissionUsersManage && <Route path="zone-user-access" element={<ZoneUserAccessTable />} />}
                {permissionParkingAdmin && <Route path="public-information" element={<ParkingDetails />} />}
                {permissionParkingAdmin && <Route path="entry-exit-points" element={<EntryExitPoints />} />}
                {permissionParkingAdmin && <Route path="points-of-interest" element={<PointsOfInterest />} />}
                {permissionParkingAdmin && <Route path="amenities" element={<Amenities />} />}
                {permissionGuestReasonsManage && <Route path="guest-reasons" element={<ReasonsForGuest />} />}
                <Route path="points-group" element={<PointsGroupTable />} />
                <Route path="allow-lists">
                    <Route path="" element={<AllowListsTable />} />
                    <Route path=":listId" element={<AllowListDashboard />} />
                </Route>
                {permissionPricing && (
                    <Route path="pricing-plans" element={<ParkingPricingPlansLayout />}>
                        <Route path="" element={<ParkingPricingPlans />} />
                        <Route path="points" element={<ParkingPricingPlansByPoints />} />
                    </Route>
                )}
                {permissionSubscriptions && <Route path="subscriptions" element={<SubscriptionPlansTable />} />}
                {permissionPricing && <Route path="pricing-plans/:pricePlanId" element={<ParkingPricingDetails />} />}
                {permissionNotificationSettings && <Route path="notifications" element={<Notifications />} />}
                {permissionParkingAdmin && <Route path="general-settings" element={<GeneralSettings />} />}
                {permissionParkingAdmin && <Route path="integrations" element={<Integrations />} />}
                {permissionParkingAdmin && <Route path="payment-providers" element={<PaymentProviders />} />}
                {permissionAdditionalServices && <Route path="additional-services" element={<AdditionalServices />} />}
                {permissionReservationsSettings && (
                    <>
                        <Route path="reservations" element={<ReservationTypesLayout />}>
                            <Route path="dynamic" element={<ReservationsTypeTable />} />
                            <Route path="fixed" element={<FixedReservationTypes />} />
                        </Route>
                        <Route path="reservations/dynamic/:reservationType" element={<SelectedReservationTypeLayout />}>
                            <Route path="" element={<ReservationTypeDetails />} />
                            <Route path="versions" element={<ReservationVersions />} />
                        </Route>
                    </>
                )}

                <Route path="*" element={<Navigate to=".." />} />
            </Route>
        </Routes>
    ) : (
        <Navigate to=".." />
    );
};

export default ParkingManagementRoutes;
